import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import type { Course } from '../../api/courseService';
import { withRoleAccess } from '../../components/auth/withRoleAccess';
import { ROLES } from '../../hooks/useAuthorization';

interface CourseFormProps {
  course?: Course;
  onSubmit: (data: { title: string; description: string; thumbnail_url?: string; is_published?: boolean }) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const FormContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.card};
  padding: 1.5rem;
  border-radius: 0.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const FormTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0.25rem;
  font-size: 1rem;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLight};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0.25rem;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLight};
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  
  input {
    margin-right: 0.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 0.75rem 1.5rem;
  background-color: ${({ theme, $primary }) => $primary ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $primary }) => $primary ? 'white' : theme.colors.text};
  border: ${({ theme, $primary }) => $primary ? 'none' : `1px solid ${theme.colors.border}`};
  border-radius: 0.25rem;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme, $primary }) => $primary ? theme.colors.primaryDark : theme.colors.background};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray};
    cursor: not-allowed;
  }
`;

const CourseForm: React.FC<CourseFormProps> = ({
  course,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [title, setTitle] = useState(course?.title || '');
  const [description, setDescription] = useState(course?.description || '');
  const [thumbnailUrl, setThumbnailUrl] = useState(course?.thumbnail_url || '');
  const [isPublished, setIsPublished] = useState(!!course?.is_published);

  useEffect(() => {
    if (course) {
      setTitle(course.title || '');
      setDescription(course.description || '');
      setThumbnailUrl(course.thumbnail_url || '');
      setIsPublished(!!course.is_published);
    }
  }, [course]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title,
      description,
      thumbnail_url: thumbnailUrl || undefined,
      is_published: isPublished,
    });
  };

  return (
    <FormContainer>
      <FormTitle>{course ? 'Edit Course' : 'Create New Course'}</FormTitle>
      
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="thumbnailUrl">Thumbnail URL (optional)</Label>
          <Input
            id="thumbnailUrl"
            type="url"
            value={thumbnailUrl}
            onChange={(e) => setThumbnailUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
          />
        </FormGroup>
        
        <FormGroup>
          <Checkbox>
            <input
              id="isPublished"
              type="checkbox"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
            />
            <Label htmlFor="isPublished" style={{ marginBottom: 0 }}>Published</Label>
          </Checkbox>
        </FormGroup>
        
        <ButtonGroup>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" $primary disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : course ? 'Update Course' : 'Create Course'}
          </Button>
        </ButtonGroup>
      </form>
    </FormContainer>
  );
};

export default withRoleAccess([ROLES.ADMIN, ROLES.APP_EDITOR])(CourseForm); 